import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Gift, CheckCircle, X, Search, Phone, Mail, Building2, User, Calendar } from 'lucide-react';

function AdminReferrals() {
    const [referrals, setReferrals] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const snap = await getDocs(query(collection(db, 'referrals'), orderBy('createdAt', 'desc')));
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setReferrals(list);
            setFiltered(list);
        } catch (e) {
            console.error('추천 목록 로드 실패:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let list = referrals;
        if (statusFilter !== 'all') {
            list = list.filter(r => (r.giftStatus || 'pending') === statusFilter);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(r =>
                (r.referrerPhone || '').toLowerCase().includes(q) ||
                (r.referrerBusinessName || '').toLowerCase().includes(q) ||
                (r.referrerName || '').toLowerCase().includes(q) ||
                (r.referrerEmail || '').toLowerCase().includes(q) ||
                (r.newUserPhone || '').toLowerCase().includes(q) ||
                (r.newUserBusinessName || '').toLowerCase().includes(q) ||
                (r.newUserEmail || '').toLowerCase().includes(q)
            );
        }
        setFiltered(list);
    }, [referrals, searchQuery, statusFilter]);

    const markAsSent = async (refId) => {
        if (!window.confirm('선물 지급 완료로 처리하시겠습니까?')) return;
        try {
            await updateDoc(doc(db, 'referrals', refId), {
                giftStatus: 'sent',
                giftSentAt: serverTimestamp(),
            });
            await load();
        } catch (e) {
            alert('처리 실패: ' + e.message);
        }
    };

    const markAsCancelled = async (refId) => {
        const reason = window.prompt('취소 사유를 입력해주세요 (선택):', '');
        if (reason === null) return;
        try {
            await updateDoc(doc(db, 'referrals', refId), {
                giftStatus: 'cancelled',
                adminNotes: reason || '',
            });
            await load();
        } catch (e) {
            alert('처리 실패: ' + e.message);
        }
    };

    const formatDate = (ts) => {
        if (!ts) return '-';
        const d = ts?.toDate ? ts.toDate() : new Date(ts);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const stats = {
        total: referrals.length,
        pending: referrals.filter(r => (r.giftStatus || 'pending') === 'pending').length,
        sent: referrals.filter(r => r.giftStatus === 'sent').length,
        cancelled: referrals.filter(r => r.giftStatus === 'cancelled').length,
    };

    return (
        <div style={S.container}>
            <div style={S.header}>
                <div>
                    <h1 style={S.title}><Gift size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} /> 추천인 관리</h1>
                    <p style={S.subtitle}>기공소 가입 시 추천한 회원 목록 · 선물 지급 처리</p>
                </div>
            </div>

            {/* 통계 */}
            <div style={S.stats}>
                <div style={{ ...S.stat, borderLeft: '4px solid #6366f1' }}>
                    <div style={S.statLabel}>전체 추천</div>
                    <div style={S.statValue}>{stats.total}건</div>
                </div>
                <div style={{ ...S.stat, borderLeft: '4px solid #f59e0b' }}>
                    <div style={S.statLabel}>지급 대기</div>
                    <div style={S.statValue}>{stats.pending}건</div>
                </div>
                <div style={{ ...S.stat, borderLeft: '4px solid #10b981' }}>
                    <div style={S.statLabel}>지급 완료</div>
                    <div style={S.statValue}>{stats.sent}건</div>
                </div>
                <div style={{ ...S.stat, borderLeft: '4px solid #94a3b8' }}>
                    <div style={S.statLabel}>취소</div>
                    <div style={S.statValue}>{stats.cancelled}건</div>
                </div>
            </div>

            {/* 검색·필터 */}
            <div style={S.toolbar}>
                <div style={S.searchBox}>
                    <Search size={16} color="#94a3b8" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="추천인/가입자 전화·이름·업체·이메일 검색"
                        style={S.searchInput}
                    />
                </div>
                <div style={S.filterTabs}>
                    {[
                        { v: 'all', l: '전체' },
                        { v: 'pending', l: '대기' },
                        { v: 'sent', l: '완료' },
                        { v: 'cancelled', l: '취소' },
                    ].map(t => (
                        <button
                            key={t.v}
                            onClick={() => setStatusFilter(t.v)}
                            style={{ ...S.tab, ...(statusFilter === t.v ? S.tabActive : {}) }}
                        >{t.l}</button>
                    ))}
                </div>
            </div>

            {/* 목록 */}
            {loading ? (
                <div style={S.empty}>로딩 중...</div>
            ) : filtered.length === 0 ? (
                <div style={S.empty}>
                    <Gift size={40} color="#cbd5e1" />
                    <p style={{ marginTop: 12 }}>해당하는 추천 기록이 없습니다.</p>
                </div>
            ) : (
                <div style={S.list}>
                    {filtered.map(r => {
                        const status = r.giftStatus || 'pending';
                        return (
                            <div key={r.id} style={S.card}>
                                <div style={S.cardHeader}>
                                    <div style={S.cardLeft}>
                                        <span style={{ ...S.statusBadge, ...statusStyle(status) }}>
                                            {statusLabel(status)}
                                        </span>
                                        <span style={S.cardDate}>
                                            <Calendar size={11} /> {formatDate(r.createdAt)}
                                        </span>
                                    </div>
                                    <div style={S.cardActions}>
                                        {status === 'pending' && (
                                            <>
                                                <button onClick={() => markAsSent(r.id)} style={{ ...S.actionBtn, background: '#10b981' }}>
                                                    <CheckCircle size={14} /> 지급 완료
                                                </button>
                                                <button onClick={() => markAsCancelled(r.id)} style={{ ...S.actionBtn, background: '#94a3b8' }}>
                                                    <X size={14} /> 취소
                                                </button>
                                            </>
                                        )}
                                        {status === 'sent' && r.giftSentAt && (
                                            <span style={S.sentInfo}>지급일: {formatDate(r.giftSentAt)}</span>
                                        )}
                                    </div>
                                </div>

                                <div style={S.cardBody}>
                                    {/* 추천인 (선물 받는 사람) */}
                                    <div style={{ ...S.party, ...S.referrerParty }}>
                                        <div style={S.partyHead}>
                                            <Gift size={14} color="#6366f1" /> 추천인 (선물 지급 대상)
                                        </div>
                                        <div style={S.partyRow}><Building2 size={13} color="#64748b" /> <strong>{r.referrerBusinessName || r.referrerName || '-'}</strong></div>
                                        <div style={S.partyRow}><Phone size={13} color="#64748b" /> {r.referrerPhone || '-'}</div>
                                        <div style={S.partyRow}><Mail size={13} color="#64748b" /> {r.referrerEmail || '-'}</div>
                                        {r.referrerName && r.referrerName !== r.referrerBusinessName && (
                                            <div style={S.partyRow}><User size={13} color="#64748b" /> {r.referrerName}</div>
                                        )}
                                    </div>

                                    <div style={S.arrow}>←</div>

                                    {/* 신규 가입자 */}
                                    <div style={{ ...S.party, ...S.newUserParty }}>
                                        <div style={S.partyHead}>
                                            <User size={14} color="#10b981" /> 추천 받아 가입한 회원
                                        </div>
                                        <div style={S.partyRow}><Building2 size={13} color="#64748b" /> <strong>{r.newUserBusinessName || r.newUserName || '-'}</strong></div>
                                        <div style={S.partyRow}><Phone size={13} color="#64748b" /> {r.newUserPhone || '-'}</div>
                                        <div style={S.partyRow}><Mail size={13} color="#64748b" /> {r.newUserEmail || '-'}</div>
                                        <div style={S.partyRow}><span style={{ fontSize: 11, color: '#94a3b8' }}>유형:</span> {r.newUserType === 'lab' ? '기공소' : r.newUserType}</div>
                                    </div>
                                </div>

                                {r.adminNotes && (
                                    <div style={S.adminNote}>
                                        📝 메모: {r.adminNotes}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const statusLabel = (s) => ({ pending: '대기', sent: '지급완료', cancelled: '취소' }[s] || s);
const statusStyle = (s) => ({
    pending: { background: '#fef3c7', color: '#92400e' },
    sent: { background: '#d1fae5', color: '#065f46' },
    cancelled: { background: '#e2e8f0', color: '#475569' },
}[s] || {});

const S = {
    container: { padding: 24, maxWidth: 1200, margin: '0 auto', fontFamily: "'Apple SD Gothic Neo', sans-serif" },
    header: { marginBottom: 24 },
    title: { fontSize: 26, fontWeight: 900, color: '#0f172a', margin: 0 },
    subtitle: { fontSize: 14, color: '#64748b', marginTop: 4 },
    stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 },
    stat: { background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    statLabel: { fontSize: 12, color: '#64748b', marginBottom: 4 },
    statValue: { fontSize: 22, fontWeight: 800, color: '#0f172a' },
    toolbar: { display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
    searchBox: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, flex: 1, minWidth: 240 },
    searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14 },
    filterTabs: { display: 'flex', gap: 6, background: '#f1f5f9', padding: 4, borderRadius: 10 },
    tab: { padding: '8px 14px', background: 'transparent', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer' },
    tabActive: { background: '#fff', color: '#0f172a', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
    list: { display: 'flex', flexDirection: 'column', gap: 14 },
    card: { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 },
    cardLeft: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    statusBadge: { padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 800 },
    cardDate: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#94a3b8' },
    cardActions: { display: 'flex', gap: 6 },
    actionBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' },
    sentInfo: { fontSize: 12, color: '#64748b' },
    cardBody: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 14, alignItems: 'stretch' },
    party: { padding: 14, borderRadius: 10, background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 6 },
    referrerParty: { background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.2)' },
    newUserParty: { background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)' },
    partyHead: { fontSize: 12, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 },
    partyRow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155' },
    arrow: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#94a3b8', fontWeight: 900 },
    adminNote: { marginTop: 12, padding: '8px 12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' },
    empty: { textAlign: 'center', padding: 60, color: '#94a3b8', background: '#fff', borderRadius: 12, fontSize: 14 },
};

export default AdminReferrals;
